import { z } from 'zod'

import { m } from '@/lib/paraglide/messages'

const activityTypes = [
    'fertilizing',
    'harvesting',
    'replanting',
    'spraying',
    'weeding',
    'watering',
    'pruning',
    'bud_picking',
    'other',
] as const

export interface CareLogSchemaOptions {
    /**
     * Hộ đang chọn CÓ khai vùng trồng hay không. Có vùng → bắt buộc chọn ít
     * nhất 1 (nhật ký phải truy được làm ở đâu). Chưa khai vùng nào → bỏ trống,
     * vì không có gì để chọn; chặn là khoá luôn nghiệp vụ của hộ đó.
     */
    requireParcels?: boolean
}

export function createCareLogSchema(options: CareLogSchemaOptions = {}) {
    return z
        .object({
            grower_id: z.string().uuid(m.validation_required()),
            activity_type: z.enum(activityTypes),
            log_date: z.string().min(1, m.validation_required()),
            quantity: z.coerce.number().min(0),
            quarantine_note: z.string().optional(),
            note: z.string().optional(),
            // Grower-area ids (vùng trồng) chosen from the household's areas.
            parcels: z.array(z.string().uuid()).default([]),
            // One entry per chosen HTX material + định lượng riêng của vật tư đó
            // (theo đơn vị của chính vật tư). Định lượng là nguồn để xuất kho.
            materials: z
                .array(
                    z.object({
                        agricultural_material_id: z
                            .string()
                            .uuid(m.care_log_material_required()),
                        quantity: z.coerce.number().min(0).default(0),
                        // Hộ tự mua ngoài → không xuất kho HTX dòng này.
                        self_bought: z.boolean().default(false),
                    }),
                )
                .default([]),
            // Ids of uploaded evidence photos (ảnh minh chứng).
            file_ids: z.array(z.string().uuid()).default([]),
            // Người thực hiện — ids of the users who did the activity. Empty is
            // allowed; the server defaults it to the current account.
            implementer_ids: z.array(z.string().uuid()).default([]),
        })
        .superRefine((val, ctx) => {
            if (options.requireParcels && val.parcels.length === 0) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    path: ['parcels'],
                    message: m.care_log_parcels_required(),
                })
            }
            // Bón phân / Phun thuốc: định lượng nhập THEO TỪNG vật tư (nguồn để
            // xuất kho), mỗi vật tư đã chọn phải > 0.
            const perMaterialQuantity =
                val.activity_type === 'fertilizing' ||
                val.activity_type === 'spraying'
            if (perMaterialQuantity) {
                // Bắt buộc chọn ít nhất 1 vật tư — không có vật tư thì phiếu
                // xuất kho lẫn trang truy xuất nguồn gốc đều trống (BE cũng chặn).
                if (val.materials.length === 0) {
                    ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        path: ['materials'],
                        message: m.care_log_materials_min(),
                    })
                }
                val.materials.forEach((mat, i) => {
                    if (!(mat.quantity > 0)) {
                        ctx.addIssue({
                            code: z.ZodIssueCode.custom,
                            path: ['materials', i, 'quantity'],
                            message: m.care_log_quantity_required(),
                        })
                    }
                })
            }
            // Hái chè giữ 1 ô định lượng chung (không có vật tư).
            if (val.activity_type === 'harvesting' && !(val.quantity > 0)) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    path: ['quantity'],
                    message: m.care_log_quantity_required(),
                })
            }
            // Cách ly KHÔNG bắt buộc. Phần lớn vật tư trong danh mục chưa điền
            // `quarantine_period` nên autofill không có gì để điền; ép nhập chỉ
            // khiến hộ gõ đại một con số cho qua cửa — số sai còn tệ hơn ô trống
            // vì nó lên thẳng trang truy xuất nguồn gốc.
        })
}

type CareLogSchema = ReturnType<typeof createCareLogSchema>

export type CareLogFormInput = z.input<CareLogSchema>
export type CareLogFormValues = z.output<CareLogSchema>
export type CareLogMaterialValue = CareLogFormValues['materials'][number]
